/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React from "react";
import { useSelector } from "react-redux";
import { setBasicDialogOpenFor } from "../../../store/helpers/helperReducers";
import { Sheet } from "../../ui/sheet";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { RadixSelectControl } from "../../ui/radix-select";
import { useStoreDispatch } from "../../../store/hooks";
import { rfFeedIndexesSelector } from "../../../store/api/deviceConfig/selectors";
import { useConnectRFFeed } from "./hooks";

const ConnectRFFeed: React.FC = () => {
    const dispatch = useStoreDispatch();

    const { rfFeedToRim, setRfFeedToRim, submitHandler, onblurHandler, errMsg, setErrMsg } = useConnectRFFeed();

    const RfFeedIndexes = useSelector(rfFeedIndexesSelector);

    const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        handleFieldChange(name, value);
    }

    const handleFieldChange = (name: string, value: string) => {
        if (name === "radioInputModuleId1") {
            if (value === "1") {
                setRfFeedToRim({ ...rfFeedToRim, radioInputModuleId2: "2", [name]: value });
            } else if (value === "3") {
                setRfFeedToRim({ ...rfFeedToRim, radioInputModuleId2: "4", [name]: value });
            }
        } else {
            setRfFeedToRim({ ...rfFeedToRim, [name]: value });
        }
        setErrMsg({ ...errMsg, [name]: "" });
    }

    const isEnableSave =
        !!rfFeedToRim.radioUnitIndex &&
        !(Object.values(errMsg).filter((item) => !!item && item).length > 0);

    const handleClose = () => {
        dispatch(setBasicDialogOpenFor(undefined));
    };

    const footer = (
        <>
            <Button variant="outline" onClick={handleClose}>Cancel</Button>
            <Button autoFocus onClick={submitHandler} disabled={!isEnableSave}>
                Save
            </Button>
        </>
    );

    return (
        <Sheet open={true} onClose={handleClose} title="Connect RF Feed" footer={footer} width="520px">
            <form autoComplete="off">
                <div className="space-y-5 pt-2">

                    <div className="space-y-2">
                        <Label htmlFor="radioUnitIndex">
                            Radio Unit Index <span className="text-destructive">*</span>
                        </Label>
                        <Input
                            id="radioUnitIndex"
                            name="radioUnitIndex"
                            required
                            autoFocus
                            onChange={onChangeHandler}
                            onBlur={onblurHandler}
                            className={errMsg?.radioUnitIndex ? "border-destructive" : ""}
                        />
                        {errMsg?.radioUnitIndex && (
                            <p className="text-sm text-destructive">{errMsg.radioUnitIndex}</p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="rfFeedIndex">RF Feed Index</Label>
                        <RadixSelectControl
                            options={RfFeedIndexes.map((v: string) => ({ value: v, label: v }))}
                            value={rfFeedToRim.rfFeedIndex}
                            onValueChange={(value) => handleFieldChange("rfFeedIndex", value)}
                            className="w-full"
                            placeholder="Select RF Feed Index"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="radioInputModuleId1">1st RadioInput Module ID</Label>
                        <RadixSelectControl
                            options={[
                                { value: "1", label: "1" },
                                { value: "3", label: "3" },
                            ]}
                            value={rfFeedToRim.radioInputModuleId1 || "1"}
                            onValueChange={(value) => handleFieldChange("radioInputModuleId1", value)}
                            className="w-full"
                            placeholder="Select module id"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="radioInputModuleId1Enable">1st RadioInput Module Enable</Label>
                        <RadixSelectControl
                            options={[
                                { value: "true", label: "true" },
                                { value: "false", label: "false" },
                            ]}
                            value={rfFeedToRim.radioInputModuleId1Enable || "true"}
                            onValueChange={(value) => handleFieldChange("radioInputModuleId1Enable", value)}
                            className="w-full"
                            placeholder="Select"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="radioInputModuleId2">2nd RadioInput Module ID</Label>
                        <RadixSelectControl
                            options={[
                                { value: "2", label: "2" },
                                { value: "4", label: "4" },
                            ]}
                            value={rfFeedToRim.radioInputModuleId2 || "2"}
                            onValueChange={(value) => handleFieldChange("radioInputModuleId2", value)}
                            className="w-full"
                            placeholder="Select module id"
                            disabled
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="radioInputModuleId2Enable">2nd RadioInput Module Enable</Label>
                        <RadixSelectControl
                            options={[
                                { value: "true", label: "true" },
                                { value: "false", label: "false" },
                            ]}
                            value={rfFeedToRim.radioInputModuleId2Enable || "true"}
                            onValueChange={(value) => handleFieldChange("radioInputModuleId2Enable", value)}
                            className="w-full"
                            placeholder="Select"
                        />
                    </div>
                </div>
            </form>
        </Sheet>
    );
};
export default ConnectRFFeed;